import React, { useEffect, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

// Animated split bar showing the A vs B breakdown
function SplitBar({ countA, countB, majorityAnswer }) {
  const total = countA + countB;
  const pctA = total > 0 ? Math.round((countA / total) * 100) : 50;
  const pctB = 100 - pctA;

  return (
    <div className="w-full mt-3">
      <div className="flex w-full h-8 rounded-full overflow-hidden text-sm font-bold">
        <div
          className="flex items-center justify-center bg-blue-500 text-white transition-all duration-700"
          style={{ width: `${pctA}%`, minWidth: pctA > 0 ? '2rem' : 0 }}
        >
          {pctA > 5 ? `${pctA}%` : ''}
        </div>
        <div
          className="flex items-center justify-center bg-orange-500 text-white transition-all duration-700"
          style={{ width: `${pctB}%`, minWidth: pctB > 0 ? '2rem' : 0 }}
        >
          {pctB > 5 ? `${pctB}%` : ''}
        </div>
      </div>
      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
        <span className={majorityAnswer === 'A' ? 'font-bold text-blue-600 dark:text-blue-400' : ''}>
          {countA} {countA === 1 ? 'person' : 'people'} {majorityAnswer === 'A' ? '👑' : ''}
        </span>
        <span className={majorityAnswer === 'B' ? 'font-bold text-orange-500 dark:text-orange-400' : ''}>
          {majorityAnswer === 'B' ? '👑' : ''} {countB} {countB === 1 ? 'person' : 'people'}
        </span>
      </div>
    </div>
  );
}

function formatChoiceText(question, choice) {
  if (!question) return choice;
  return `${choice}: ${choice === 'A' ? question.optionA : question.optionB}`;
}

function getChoiceResultClasses(choice) {
  return choice === 'A'
    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
    : 'border-orange-200 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-900/30 dark:text-orange-200';
}

// A single option button used in the answering phase
function OptionButton({ label, option, selected, onSelect, colorClass, baseClass, hoverClass, showSelectedIcon = true }) {
  const isSelected = selected === option;
  return (
    <button
      onClick={() => onSelect(option)}
      disabled={!!selected}
      className={`
        flex-1 flex flex-col items-center justify-center gap-2 p-4 rounded-2xl border-2 text-center
        transition-all duration-200 font-semibold text-base
        ${isSelected
          ? `${colorClass} border-transparent text-white scale-105 shadow-lg`
          : `${baseClass} hover:scale-102 hover:shadow-md ${hoverClass}`
        }
        ${selected && !isSelected ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span className="text-4xl leading-none">{option}</span>
      <span>{label}</span>
      {isSelected && showSelectedIcon && <span className="text-lg">✓</span>}
    </button>
  );
}

export default function WouldYouRatherGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, onGameAction }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, roomId });

  // Two-step answering: first own answer, then prediction
  const [ownAnswer, setOwnAnswer] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(gameState?.timeLimit || 30);

  const phase = gameState?.phase || 'waiting';
  const round = gameState?.round || 0;
  const completedRounds = Array.isArray(gameState?.results) ? gameState.results.length : 0;
  const displayRound = phase === 'answering'
    ? Math.max(round, completedRounds + 1)
    : phase === 'reveal'
      ? Math.max(round, completedRounds)
      : round;
  const hasActiveRound = displayRound > 0;
  const startTime = typeof gameState?.startTime === 'number' ? gameState.startTime : null;
  const timeLimit = gameState?.timeLimit || 30;

  // Reset local state on new round
  useEffect(() => {
    if (phase === 'answering') {
      setOwnAnswer(null);
      setPrediction(null);
      setSubmitted(false);
    }
  }, [phase, round]);

  // Animated countdown using server startTime
  useEffect(() => {
    setTimeLeft(timeLimit);
    if (!['answering', 'reveal'].includes(phase) || startTime === null) return undefined;

    let raf = null;
    const tick = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      const remaining = Math.max(0, Math.ceil(timeLimit - elapsed));
      setTimeLeft(remaining);
      if (remaining > 0 && ['answering', 'reveal'].includes(phase)) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [phase, startTime, timeLimit]);

  const doSubmit = async (ans, pred) => {
    setSubmitted(true);
    const action = { type: 'submit', answer: ans, prediction: pred };
    if (typeof onGameAction === 'function') onGameAction(action);
    else await submitAction(action);
  };

  const currentQuestion = gameState?.currentQuestion || null;
  const lastResult = gameState?.results?.length > 0 ? gameState.results[gameState.results.length - 1] : null;
  const players = gameState?.players || [];
  const scores = gameState?.scores || {};
  const submissionCount = gameState?.submissions ? Object.keys(gameState.submissions).length : 0;
  const isHeadToHead = players.length === 2;
  const opponent = isHeadToHead ? players.find((player) => player.socketId !== user?.socketId) : null;
  const opponentSubmission = lastResult && opponent ? lastResult.submissions?.[opponent.socketId] : null;
  const showMajorityReveal = lastResult?.predictionMode !== 'opponent';
  const predictionPrompt = isHeadToHead ? 'your opponent' : 'most people';

  return (
    <div className="wouldyourather-game flex items-center justify-center min-h-full">
      <div className="w-full max-w-xl p-4">

        {/* Header */}
        <div className="mb-3 flex items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          {phase !== 'finished' && hasActiveRound && (
            <div>
              Round {displayRound} of {gameState?.rounds || '?'}
            </div>
          )}
          {['answering', 'reveal'].includes(phase) && (
            <div className="rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
              {timeLeft}s
            </div>
          )}
        </div>

        {/* ANSWERING PHASE */}
        {phase === 'answering' && currentQuestion && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-5">
            <div className="text-center text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
              Would you rather…
            </div>

            {/* Countdown */}
            <div className="mb-4">
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-400 dark:bg-indigo-500 transition-none"
                  style={{ width: `${(timeLeft / (timeLimit || 30)) * 100}%`, transition: 'width 0.1s linear' }}
                />
              </div>
            </div>

            {!submitted ? (
              <>
                {/* Step 1: pick own answer */}
                {!ownAnswer && (
                  <div>
                    <div className="flex gap-3">
                      <OptionButton
                        label={currentQuestion.optionA}
                        option="A"
                        selected={ownAnswer}
                        onSelect={setOwnAnswer}
                        colorClass="bg-blue-500"
                        baseClass="bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200"
                        hoverClass="hover:bg-blue-100 hover:border-blue-400 hover:text-blue-800 dark:hover:bg-blue-900/40 dark:hover:border-blue-500 dark:hover:text-blue-100"
                        showSelectedIcon={false}
                      />
                      <div className="flex items-center font-bold text-gray-400 dark:text-gray-500 text-sm flex-shrink-0">OR</div>
                      <OptionButton
                        label={currentQuestion.optionB}
                        option="B"
                        selected={ownAnswer}
                        onSelect={setOwnAnswer}
                        colorClass="bg-orange-500"
                        baseClass="bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-200"
                        hoverClass="hover:bg-orange-100 hover:border-orange-400 hover:text-orange-800 dark:hover:bg-orange-900/40 dark:hover:border-orange-500 dark:hover:text-orange-100"
                        showSelectedIcon={false}
                      />
                    </div>
                    <div className="mt-3 text-center text-sm font-semibold text-gray-500 dark:text-gray-400">
                      What would <span className="font-bold">YOU</span> choose?
                    </div>
                  </div>
                )}

                {/* Step 2: predict what the group will choose */}
                {ownAnswer && !prediction && (
                  <div>
                    <div className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
                      You chose: <span className={ownAnswer === 'A' ? 'text-blue-500' : 'text-orange-500'}>
                        {formatChoiceText(currentQuestion, ownAnswer)}
                      </span>
                    </div>
                    <div className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 mt-3">
                      Now predict what <span className="text-purple-500 dark:text-purple-400">{predictionPrompt}</span> will pick:
                    </div>
                    <div className="flex gap-3">
                      <OptionButton
                        label={currentQuestion.optionA}
                        option="A"
                        selected={prediction}
                        onSelect={(opt) => { setPrediction(opt); doSubmit(ownAnswer, opt); }}
                        colorClass="bg-blue-500"
                        baseClass="bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-200"
                        hoverClass="hover:bg-blue-100 hover:border-blue-400 hover:text-blue-800 dark:hover:bg-blue-900/40 dark:hover:border-blue-500 dark:hover:text-blue-100"
                      />
                      <div className="flex items-center font-bold text-gray-400 dark:text-gray-500 text-sm flex-shrink-0">OR</div>
                      <OptionButton
                        label={currentQuestion.optionB}
                        option="B"
                        selected={prediction}
                        onSelect={(opt) => { setPrediction(opt); doSubmit(ownAnswer, opt); }}
                        colorClass="bg-orange-500"
                        baseClass="bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-900/20 dark:border-orange-800 dark:text-orange-200"
                        hoverClass="hover:bg-orange-100 hover:border-orange-400 hover:text-orange-800 dark:hover:bg-orange-900/40 dark:hover:border-orange-500 dark:hover:text-orange-100"
                      />
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <div className="text-4xl mb-3">⏳</div>
                <div className="text-gray-600 dark:text-gray-300 font-semibold">Answer locked in!</div>
                <div className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Waiting for others… ({submissionCount} / {players.length} answered)
                </div>
              </div>
            )}

            {/* Waiting indicator while answering */}
            {!submitted && (
              <div className="mt-4 text-center text-xs text-gray-400 dark:text-gray-500">
                {submissionCount} / {players.length} answered
              </div>
            )}
          </div>
        )}

        {/* REVEAL PHASE */}
        {phase === 'reveal' && lastResult && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-5">
            {/* Question */}
            {lastResult.question && (
              <div className="text-center mb-4">
                <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-1">The question was</div>
                <div className="font-bold text-gray-800 dark:text-gray-100">
                  <span className="text-blue-500">A: {lastResult.question.optionA}</span>
                  <div><span className="mx-2 text-gray-400">vs</span></div>
                  <span className="text-orange-500">B: {lastResult.question.optionB}</span>
                </div>
              </div>
            )}

            {/* Split bar */}
            <SplitBar
              countA={lastResult.countA}
              countB={lastResult.countB}
              majorityAnswer={lastResult.majorityAnswer}
            />

            {/* Majority verdict */}
            <div className="mt-4 text-center">
              {showMajorityReveal && lastResult.majorityAnswer ? (
                <div className={`inline-block rounded-full border px-4 py-2 text-sm font-bold shadow ${getChoiceResultClasses(lastResult.majorityAnswer)}`}>
                  🏆 Majority chose: {lastResult.majorityAnswer === 'A'
                    ? formatChoiceText(lastResult.question, 'A')
                    : formatChoiceText(lastResult.question, 'B')}
                </div>
              ) : showMajorityReveal ? (
                <div className="inline-block px-4 py-2 rounded-full font-bold text-white text-sm bg-gray-500 shadow">
                  🤝 It's a tie! No points awarded
                </div>
              ) : opponentSubmission ? (
                <div className={`inline-block rounded-full border px-4 py-2 text-sm font-bold shadow ${getChoiceResultClasses(opponentSubmission.answer)}`}>
                  🎯 {opponent?.username || 'Opponent'} picked: {formatChoiceText(lastResult.question, opponentSubmission.answer)}
                </div>
              ) : (
                <div className="inline-block px-4 py-2 rounded-full font-bold text-white text-sm bg-gray-500 shadow">
                  ⏳ Opponent answer not submitted
                </div>
              )}
            </div>

            {/* Player breakdown */}
            <div className="mt-4 space-y-2">
              {players.map(p => {
                const sub = lastResult.submissions?.[p.socketId];
                const awarded = lastResult.awards?.[p.socketId] || 0;
                const predictedCorrectly = awarded > 0;
                const isMe = p.socketId === user?.socketId;
                return (
                  <div
                    key={p.socketId}
                    className={`flex items-center justify-between p-2 rounded-xl border text-sm
                      ${predictedCorrectly
                        ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}
                    `}
                  >
                    <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200 font-medium">
                      {p.username || p.socketId}
                      {isMe && <span className="text-indigo-500 text-xs">(you)</span>}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      {sub ? (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold text-white
                          ${sub.answer === 'A' ? 'bg-blue-500' : 'bg-orange-500'}`}>
                          {sub.answer}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">no answer</span>
                      )}
                      {sub && (
                        <span className="text-xs text-gray-400">
                          predicted: <span className={sub.prediction === 'A' ? 'text-blue-500' : 'text-orange-500'}>
                            {sub.prediction}
                          </span>
                        </span>
                      )}
                      {predictedCorrectly && (
                        <span className="text-green-600 dark:text-green-400 font-bold text-xs">+{awarded} pts 🎉</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reveal countdown */}
            <div className="mt-4">
              <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-400 dark:bg-indigo-500 transition-none"
                  style={{ width: `${(timeLeft / (timeLimit || 9)) * 100}%`, transition: 'width 0.1s linear' }}
                />
              </div>
              <div className="text-xs text-right text-gray-400 dark:text-gray-500 mt-1">
                {gameState?.finished ? 'Final scores coming up…' : 'Next question in'} {timeLeft}s
              </div>
            </div>
          </div>
        )}

        {/* FINISHED PHASE */}
        {phase === 'finished' && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-5">
            <div className="text-center mb-5">
              <div className="text-4xl mb-2">🏆</div>
              <div className="text-xl font-bold text-gray-800 dark:text-gray-100">Game Over!</div>
              {gameState?.result?.winnerNames && (
                <div className="text-indigo-500 dark:text-indigo-400 font-semibold mt-1">
                  {gameState.result.winners?.length > 1 ? 'Winners' : 'Winner'}: {gameState.result.winnerNames} 🎊
                </div>
              )}
            </div>
            <div className="space-y-2">
              {[...players]
                .sort((a, b) => (scores[b.socketId] || 0) - (scores[a.socketId] || 0))
                .map((p, i) => {
                  const isMe = p.socketId === user?.socketId;
                  const score = scores[p.socketId] || 0;
                  const topScore = Math.max(...players.map(pl => scores[pl.socketId] || 0));
                  const isWinner = score === topScore && topScore > 0;
                  return (
                    <div
                      key={p.socketId}
                      className={`flex items-center justify-between p-3 rounded-xl border font-medium
                        ${isWinner
                          ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-600'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}
                      `}
                    >
                      <div className="flex items-center gap-2 text-gray-800 dark:text-gray-200">
                        <span className="text-gray-400 dark:text-gray-500 text-sm w-5">#{i + 1}</span>
                        {isWinner && <span>🏆</span>}
                        {p.username || p.socketId}
                        {isMe && <span className="text-xs text-indigo-500">(you)</span>}
                      </div>
                      <div className="text-gray-700 dark:text-gray-300 font-bold">{score} pts</div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

WouldYouRatherGame.Guide = function WouldYouRatherGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Would You Rather</h3>
      <p className="mb-2">
        A classic ice-breaker game! Each round, players are given a "Would You Rather" dilemma with two options.
      </p>
      <p className="mb-2">
        <strong>Step 1:</strong> Pick which option <em>you</em> would choose. 🤔
      </p>
      <p className="mb-2">
        <strong>Step 2:</strong> In 2-player games, predict what your <em>opponent</em> will pick. With 3 or more players, predict what the <em>majority</em> will pick. 🎯
      </p>
      <p className="mb-2">
        <strong>Scoring:</strong> If your prediction matches the opponent in 2-player games or the majority in larger games, you earn <strong>10 points</strong>!
        In 3+ player games, ties award no points.
      </p>
      <p className="mb-1 text-sm text-gray-500">
        Tip: Sometimes it's smarter to predict what others think, even if you'd personally choose differently!
      </p>
    </div>
  );
};
